import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AthleteLayout from "../common/AthleteLayout";
import { AthleteHeader } from "../common/AthleteHeader";

const getYouTubeThumbnail = (url) => {
  if (!url) return null;
  const regExp = /(?:youtube\.com\/.*v=|youtu\.be\/)([^"&?/\s]{11})/;
  const match = url.match(regExp);
  if (!match) return null;
  return `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`;
};

export const AthleteStrengthWorkoutList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { product_basic, weekNumber, dayName, items } = location.state || {};

  if (!items || items.length === 0) {
    return (
      <AthleteLayout>
        <AthleteHeader />
        <p className="text-center text-black py-10">No workout data found</p>
      </AthleteLayout>
    );
  }

  const handleBackClick = () =>
    navigate(
      `/athlete/strength-product/strength-workout/${product_basic._id}`,
      {
        state: {
          product_basic,
          openIndex: weekNumber - 1, // restore accordion position
        },
      },
    );

  /** Send full parent exercise object to next screen */
  const goToExercise = (exerciseObj) => {
    if (!exerciseObj?.exercise_id?._id) return;

    navigate(
      `/athlete/strength-product/strength-workout/day/exercise/${exerciseObj.exercise_id._id}`,
      {
        state: {
          product_basic,
          weekNumber,
          dayName,
          exercise_id: exerciseObj.exercise_id._id,
          exercise: exerciseObj, // FULL object including sets
        },
      },
    );
  };

  return (
    <AthleteLayout>
      <div className="h-full flex flex-col">
        <div className="flex-1 overflow-y-auto">
          <div className="bg-white min-h-screen">
            {/* HEADER */}
            <div className="w-full max-w-2xl mx-auto pt-3 px-5 bg-white">
              <div className="relative py-4 text-center border-b border-black/30 mb-4">
                <span
                  className="absolute left-0 top-1/2 -translate-y-1/2 flex items-center gap-2 cursor-pointer text-black"
                  onClick={handleBackClick}
                >
                  <BackArrow />
                </span>
                <p className="text-black text-lg font-bold">
                  {product_basic?.title}
                </p>
              </div>
              <div className="bg-black text-white font-medium text-lg xs:text-xl flex items-center gap-2 py-3 px-4 rounded-2xl mb-2">
                <p className="text-base xs:text-xl font-medium">
                  W{weekNumber}
                </p>
                <p className="text-base xs:text-xl font-medium">|</p>
                <p className="text-base xs:text-xl font-medium capitalize">{dayName}</p>
              </div>
            </div>

            {/* LIST */}
            <div className="w-full max-w-2xl mx-auto pt-1 px-5 bg-white">
              {items.map((item, idx) => {
                const isMulti =
                  item.type === "superset" || item.type === "circuit";
                const exercises = isMulti
                  ? item[item.type]?.exercises || []
                  : [];

                /** COLOR FOR THE LEFT BAR */
                const barColor =
                  item.type === "exercise"
                    ? "bg-blue-500"
                    : item.type === "superset"
                      ? "bg-purple-500"
                      : "bg-orange-500";

                /** ------------------------------
                 * SINGLE NORMAL EXERCISE ROW
                 * ------------------------------ */
                if (!isMulti) {
                  return (
                    <div
                      key={idx}
                      className="flex items-center gap-4 py-4 border-b border-black/30 cursor-pointer"
                      onClick={() => goToExercise(item.exercise)}
                    >
                      <div
                        className={`w-1 h-[60px] rounded-full  bg-darkYellow`}
                      />

                      <div className="size-10 xs:size-14 overflow-hidden bg-gray-200">
                        <img
                          src={getYouTubeThumbnail(
                            item.exercise.exercise_id.video_url,
                          )}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      <p className="text-black font-medium text-base xs:text-xl leading-tight capitalize">
                        {item.exercise.exercise_id.title}
                      </p>
                    </div>
                  );
                }

                /** ------------------------------
                 * SUPERSET / CIRCUIT GROUP
                 * ------------------------------ */

                return (
                  <div key={idx} className="border-b border-black/30 py-3">
                    <div className="pl-5">
                      <div className="py-2 px-3 bg-smock rounded-xl mb-2">
                        <p className="text-black font-light text-xs capitalize">
                          {item.type === "superset"
                            ? item.superset.title || "Superset"
                            : item.circuit.title || "Circuit"}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-row gap-4">
                      {/* ONE LONG VERTICAL BAR FOR ENTIRE GROUP */}
                      <div className={`w-1 rounded-full bg-darkYellow`} />

                      {/* RIGHT SIDE (HEADER + ALL EXERCISES) */}
                      <div className="flex-1">
                        {/* HEADER */}

                        {/* CHILD EXERCISES — SAME AS NORMAL ROWS, PERFECT ALIGNMENT */}
                        {exercises.map((ex, idy) => (
                          <div
                            key={idy}
                            className="flex items-center gap-4 py-4 cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              goToExercise(ex);
                            }}
                          >
                            {/* SAME BLUE BAR AS NORMAL EXERCISES */}

                            <div className="size-10 xs:size-14 overflow-hidden bg-gray-200">
                              <img
                                src={getYouTubeThumbnail(
                                  ex.exercise_id.video_url,
                                )}
                                className="w-full h-full object-cover"
                              />
                            </div>

                            <p className="text-black font-medium text-base xs:text-xl leading-tight capitalize">
                              {ex.exercise_id.title}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </AthleteLayout>
  );
};

const BackArrow = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 19l-7-7 7-7"
    />
  </svg>
);
